import { useState } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useMixpanel } from "react-mixpanel-browser";

import { GetOrders } from "./queries";
import { ExportOrders, UpdateOrderStatus } from "./mutations";

import _ from "lodash";
import moment from "moment";

import { ORDER_QUERY_LIMIT } from "./constants";
import { buildFilterOptions, buildGetOrdersVariables } from "./helpers";
import useTutorialsState from "../../hooks/useTutorialsState";

export function useOrderList({ sort, filters }) {
  const { loading, data, fetchMore, refetch } = useQuery(GetOrders, {
    ...buildGetOrdersVariables({ sort, filters }),
    fetchPolicy: "network-only"
  });

  const orders = loading
    ? []
    : data.orders.map((order) => ({
        ...order,
        amount: `$${order.amount.toFixed(2)}`,
        createdAt: moment(order.createdAt).format("MMM D, YYYY hh:mm A"),
        address: `${order.destination.street1}, ${order.destination.city}, ${order.destination.state.name} ${order.destination.postalCode}`,
        employerAddress:
          order.employerAddress &&
          `${order.employerAddress.street1}, ${order.employerAddress.city}, ${order.employerAddress.state.name} ${order.employerAddress.postalCode}`,
        buyer: order.destination.name,
        seller: order.seller.name,
        name: order.product.name,
        quantity: order.quantity,
        variant: order.product.selectedValues
          .map((elem) => elem.name)
          .toString()
          .split(",")
          .join(", ") || 'N/A'
      }));

  const {
    loadingMore,
    onEndReached,
    setPreventRefetch
  } = useOrderListPagination({
    orders,
    isOrdersLoading: loading,
    fetchMore,
    sort,
    filters
  });

  return {
    loadingMore,
    loading,
    orders,
    setPreventRefetch,
    refetch,
    onEndReached
  };
}

export function useExportOrders({ sort, filters }) {
  const mixpanel = useMixpanel();

  const [exportOrders, { loading: isExportLoading }] = useMutation(
    ExportOrders
  );

  const onExportPress = async () => {
    try {
      const result = await exportOrders(
        buildGetOrdersVariables({ sort, filters })
      );

      mixpanel.track("Exported Order List CSV");

      const downloadTab = window.open(result.data.exportOrders.url, "_blank");
      downloadTab.focus();
    } catch (e) {}
  };

  return { onExportPress, isExportLoading };
}

export function useFilters() {
  const [filters, setFilters] = useState({});
  const { data, loading } = useQuery(GetOrders);
  const filterOptions = buildFilterOptions({ orders: data });

  const mixpanel = useMixpanel();

  return {
    filters,
    filterOptions,
    setFilters: (newFilters) => {
      setFilters(newFilters);

      const Filters = Object.entries(newFilters).map(
        ([filterKey, filterValue]) => ({
          Title: filterOptions.find(
            (filterOption) => filterOption.key === filterKey
          ).label,
          Setting: filterValue.label || filterValue
        })
      );

      mixpanel.track("Filtered Order List", { Filters });
    },
    isFiltersLoading: loading
  };
}

export function useUpdateOrderStatus({ refetch }) {
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const [updateOrderStatus, { loading: isStatusLoading }] = useMutation(
    UpdateOrderStatus
  );

  const onStatusChange = async ({ status, id }) => {
    await updateOrderStatus({
      variables: { id, status }
    });

    const hasManagedOrders = getHasCompletedTutorial("Manage Order Status");

    if (!hasManagedOrders) {
      await completeTutorial({
        variables: { name: "Manage Order Status", version: 1 }
      });
    }

    await refetch();
  };

  return { onStatusChange };
}

export function useOrderListPagination({
  orders,
  isLoading,
  filters,
  fetchMore,
  sort
}) {
  const [preventRefetch, setPreventRefetch] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const onEndReached = async () => {
    if (isLoading || preventRefetch || loadingMore) return;

    setLoadingMore(true);

    await fetchMoreOrders({
      orders,
      sort,
      filters,
      fetchMore,
      setPreventRefetch
    });

    setLoadingMore(false);
  };

  return {
    onEndReached,
    loadingMore,
    setPreventRefetch
  };
}

export const fetchMoreOrders = async ({
  orders,
  sort,
  filters,
  fetchMore,
  setPreventRefetch
}) => {
  await fetchMore({
    ...buildGetOrdersVariables({
      sort,
      filters,
      cursor: orders.slice(-1)[0]
    }),
    updateQuery: (previousResult, { fetchMoreResult }) => {
      if (fetchMoreResult.orders.length < ORDER_QUERY_LIMIT) {
        setPreventRefetch(true);
      }

      return {
        ...previousResult,
        orders: [...previousResult.orders, ...fetchMoreResult.orders]
      };
    }
  });
};
